import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { SpeechMarkup } from 'shared/src/types/speech-markup';
import type { RiveAnimationConfig } from 'shared/src/types/rive-animation-config';
import { AudioTrack } from '../../../../shared/src/components/audio-track/audio-track.component';
import { AiChatMessage } from '../components/ai-chat/ai-chat.component';

export interface QuizOption {
  id: string; // Required: unique ID for this option (used for logic and analytics)
  title?: string; // Localized display text
  emoji?: string;
  remixIcon?: string;
  imageFilePath?: string;
  imageMode?: 'cover' | 'center';
  rive?: RiveAnimationConfig;
  isNextIconShown?: boolean;
}

export interface ResultPlanCard {
  icon: string;
  text: string;
}

export interface Quiz {
  title: string;
  subtitle?: string;
  isQuizBadgeShown?: boolean; //this is to show quiz badge in case we start right from questions
  options?: QuizOption[];
  isTilesDisplay?: boolean; //in case we want to show options as tiles
  isChips?: boolean;
  imageConfig?: {
    path: string;
    maxWidth?: string;
  };
  rive?: RiveAnimationConfig;
  videoFilePath?: string;
  mediaCite?: string;
  messages?: AiChatMessage[]; //this is for chat like messages
  planCards?: ResultPlanCard[]; // static cards under the rive and its caption
  topIcon?: string;
  audioTracks?: AudioTrack[];
  // Locale-independent id of the sample a voting step plays (book manifest key or demo id),
  // reported as `asset_id` so sample performance can be broken down per book / text.
  assetId?: string;
  isVoting?: boolean;
  hasSocialProof?: boolean; //it should be either social proof or review not both
  isMentionedInShown?: boolean; // "Mentioned in:" press logos under the step, as on the intro screen
  userReview?: UserReview;
  isMultiple: boolean;
  isRequired: boolean;
  comparison?: {
    opponent: string;
    rows: {
      feature: string;
      peech: boolean;
      opponent: boolean;
    }[];
    footnote?: string;
  };
  // Optional text demo configuration for word-level highlighting playback
  textDemo?: {
    audioAssetPath: string; // path under assets to corresponding MP3
    fullText: string; // full text to display alongside markups
    markups: SpeechMarkup[]; // pre-imported markups
  };
}

export const highlightText = (text: string, isRed: boolean = false) => {
  const className = isRed ? 'red-color' : 'main-color';
  return `<span class="${className}">${text}</span>`;
};

// Import $localize for runtime translation
declare const $localize: any;

/**
 * ============================================================================
 * ID-BASED QUIZ OPTIONS
 * Keys = IDs (used for logic), Values = Localized display strings
 * ============================================================================
 */

// Goals
export const goals = {
  beMoreProductive: $localize`:@@goalProductive:⚡ Be more productive`,
  learnSomethingNew: $localize`:@@goalLearn:📚 Learn something new`,
  listenOnTheGo: $localize`:@@goalListenGo:🚶 Listen on the go`,
  makeReadingEasier: $localize`:@@goalReading:📱 Make reading easier`,
  adhdEyeStrainDyslexia: $localize`:@@goalAdhd:💪 ADHD / Eye strain / Dyslexia`,
  everythingIsPerfect: $localize`:@@goalPerfect:😎 Everything is perfect`,
} as const;

export type GoalId = keyof typeof goals;

// Motivations
export const motivations = {
  advanceCareer: $localize`:@@motivCareer:💼 Advance my career`,
  excelEducation: $localize`:@@motivEducation:🎓 Excel in my education`,
  supportMentalHealth: $localize`:@@motivMentalHealth:🧠 Support my mental health`,
  manageAdhd: $localize`:@@motivAdhd:💊 Manage ADHD symptoms`,
  overcomeDyslexia: $localize`:@@motivDyslexia:🔤 Overcome dyslexia challenges`,
  improveFocus: $localize`:@@motivFocus:🎯 Improve focus & attention`,
  saveTime: $localize`:@@motivTime:⏰ Save time in my daily routine`,
  enjoyEntertainment: $localize`:@@motivEntertainment:🎵 Enjoy inspiring entertainment`,
  personalGrowth: $localize`:@@motivGrowth:🚀 Personal growth & self-improvement`,
  reduceEyeStrain: $localize`:@@motivEyeStrain:👀 Reduce eye strain & screen time`,
} as const;

export type MotivationId = keyof typeof motivations;

export const readerGoals = {
  relaxation: '😌 ' + $localize`:@@readerReasonRelax:Relaxation and fun`,
  personalGrowth: '🌱 ' + $localize`:@@readerReasonGrowth:Personal growth`,
  learningSkills: '💡 ' + $localize`:@@readerReasonSkills:Learning new skills`,
  schoolStudy: '🎓 ' + $localize`:@@readerReasonStudy:School/study`,
  work: '💼 ' + $localize`:@@readerReasonWork:Work`,
  languagePractice: '🌍 ' + $localize`:@@readerReasonLanguage:Language practice`,
} as const;

export type ReaderGoalId = keyof typeof readerGoals;

// Challenges
export const challenges = {
  noFreeTime: $localize`:@@challengeNoTime:⏳ No free time`,
  stuckInRoutine: $localize`:@@challengeRoutine:🔄 Stuck in routine`,
  constantStress: $localize`:@@challengeStress:😰 Constant stress`,
  toxicEnvironment: $localize`:@@challengeToxic:🤢 Toxic environment`,
  procrastination: $localize`:@@challengeProcrastination:🪙 Procrastination`,
  lackOfMotivation: $localize`:@@challengeMotivation:🧍 Lack of motivation`,
  lowSelfEsteem: $localize`:@@challengeSelfEsteem:💪 Low Self-Esteem`,
  notSureWhereToStart: $localize`:@@challengeStart:🧩 Not sure where to start`,
  everythingIsPerfect: $localize`:@@challengePerfect:😎 Everything is perfect`,
} as const;

export type ChallengeId = keyof typeof challenges;

// Gender
export const genders = {
  male: $localize`:@@genderMale:👱‍♂️ Male`,
  female: $localize`:@@genderFemale:👩 Female`,
  other: $localize`:@@genderOther:🧓 Other`,
} as const;

export type GenderId = keyof typeof genders;

// Age ranges
export const ageRanges = {
  under21: $localize`:@@ageUnder21:Age: < 21`,
  age2140: $localize`:@@age2140:Age: 21-40`,
  age4154: $localize`:@@age4154:Age: 41-54`,
  age55plus: $localize`:@@age55plus:Age: 55+`,
  preferNotToTell: $localize`:@@agePreferNotTell:🤐 I prefer not to tell`,
} as const;

export type AgeRangeId = keyof typeof ageRanges;

export const professionalStatuses = {
  employedFullTime: $localize`:@@profEmployedFull:👔 Employed Full-Time`,
  selfEmployed: $localize`:@@profSelfEmployed:💼 Self-Employed/Entrepreneur`,
  freelancer: $localize`:@@profFreelancer:👨‍💻 Freelancer/Consultant`,
  homemaker: $localize`:@@profHomemaker:🏠 Homemaker`,
  continuingEducation: $localize`:@@profEducation:🎓 Continuing Education`,
  retired: $localize`:@@profRetired:👴 Retired`,
  seekingOpportunities: $localize`:@@profSeeking:🔎 Seeking New Opportunities`,
} as const;

export type ProfessionalStatusId = keyof typeof professionalStatuses;

export const distractionLevels = {
  extremelyEasily: $localize`:@@distractExtremely:📱 Extremely easily`,
  veryEasily: $localize`:@@distractVery:💻 Very easily`,
  somewhatEasily: $localize`:@@distractSomewhat:😅 Somewhat easily`,
  rarely: $localize`:@@distractRarely:🧐 Rarely`,
  never: $localize`:@@distractNever:🙅 Never`,
} as const;

export type DistractionLevelId = keyof typeof distractionLevels;

export const anxietyLevels = {
  yesSignificant: $localize`:@@anxietySignificant:😰 Yes, it's significant`,
  yesSomeExtent: $localize`:@@anxietySomeExtent:😟 Yes, to some extent`,
  occasionally: $localize`:@@anxietyOccasionally:😬 Occasionally`,
  rarely: $localize`:@@anxietyRarely:🙂 Rarely`,
  never: $localize`:@@anxietyNever:😌 Never`,
} as const;

export type AnxietyLevelId = keyof typeof anxietyLevels;

export const procrastinationFrequencies = {
  always: $localize`:@@procrastAlways:🐌 Always`,
  frequently: $localize`:@@procrastFrequently:🐢 Frequently`,
  sometimes: $localize`:@@procrastSometimes:🐇 Sometimes`,
  rarely: $localize`:@@procrastRarely:🦅 Rarely`,
  never: $localize`:@@procrastNever:🚀 Never`,
} as const;

export type ProcrastinationFrequencyId = keyof typeof procrastinationFrequencies;

export const technologyReadiness = {
  veryExcited: $localize`:@@techExcited:🤩 Very excited and open`,
  openCautious: $localize`:@@techOpenCautious:😊 Open, but cautious`,
  neutral: $localize`:@@techNeutral:😐 Neutral`,
  preferTraditional: $localize`:@@techTraditional:📘 Prefer traditional methods`,
  avoidTechnology: $localize`:@@techAvoid:🙅 Avoid using technology`,
} as const;

export type TechnologyReadinessId = keyof typeof technologyReadiness;

export const audioExperiences = {
  yesRegularly: $localize`:@@audioExpRegularly:🎧 Yes, regularly`,
  yesOccasionally: $localize`:@@audioExpOccasionally:🎙️ Yes, occasionally`,
  noButInterested: $localize`:@@audioExpInterested:👂 No, but I'm interested`,
  noNotReally: $localize`:@@audioExpNotReally:🤔 No, not really`,
} as const;

export type AudioExperienceId = keyof typeof audioExperiences;

export const personTypes = {
  extrovert: $localize`:@@personExtrovert:🤗 Extrovert`,
  introvert: $localize`:@@personIntrovert:😌 Introvert`,
  both: $localize`:@@personBoth:🤔 Both`,
} as const;

export type PersonTypeId = keyof typeof personTypes;

export const visionCareOptions = {
  gettingWorse: $localize`:@@visionGettingWorse:👀 Yeah, it's getting worse`,
  aBit: $localize`:@@visionABit:📉 A bit`,
  allGood: $localize`:@@visionAllGood:🧘 Nope, all good`,
} as const;

export type VisionCareId = keyof typeof visionCareOptions;

export const timeForReadingOptions = {
  allTheTime: $localize`:@@allTheTime:😩 All the time`,
  sometimes: $localize`:@@sometimes:🙄 Sometimes`,
  notReally: $localize`:@@notReally:😎 Not really`,
} as const;

export type TimeForReadingId = keyof typeof timeForReadingOptions;

export const kindleFrictionOptions = {
  noQuietTime: $localize`:@@kindleFrictionNoQuietTime:I rarely get quiet reading time`,
  fallAsleep: $localize`:@@kindleFrictionFallAsleep:I fall asleep before finishing a chapter`,
  longChapters: $localize`:@@kindleFrictionLongChapters:Long chapters are hard to start`,
  wantHandsFree: $localize`:@@kindleFrictionHandsFree:I want to listen while walking or doing chores`,
  voicesMatter: $localize`:@@kindleFrictionVoicesMatter:I need voices that do not sound robotic`,
} as const;

export type KindleFrictionId = keyof typeof kindleFrictionOptions;

export const retentionOptions = {
  yesAllTime: $localize`:@@retentionAllTime:🤔 Yes, all the time`,
  yesOften: $localize`:@@retentionOften:😕 Yes, often`,
  sometimes: $localize`:@@retentionSometimes:😐 Sometimes`,
  rarely: $localize`:@@retentionRarely:😊 Rarely`,
  never: $localize`:@@retentionNever:🤓 Never`,
} as const;

export type RetentionId = keyof typeof retentionOptions;

export const investmentTimes = {
  noFreeTime: $localize`:@@investNoFreeTime:🤙 Don't have free time`,
  microSession: $localize`:@@investMicroSession:👌 Have time for a micro-session`,
  fewSessions: $localize`:@@investFewSessions:🤘 Can give it a chance for a few sessions`,
  regularCommitment: $localize`:@@investRegularCommit:💪 Ready for regular commitment`,
} as const;

export type InvestmentTimeId = keyof typeof investmentTimes;

export const listeningTimes = {
  minutes0515: $localize`:@@minutes0515:5-15 minutes / day`,
  minutes1530: $localize`:@@minutes1530:15-30 minutes / day`,
  minutes3060: $localize`:@@minutes3060:30-60 minutes / day`,
  hours123: $localize`:@@hours123:1-2 hours / day`,
  hours2plus: $localize`:@@hours2plus:2+ hours / day`,
} as const;

export type ListeningTimeId = keyof typeof listeningTimes;

export const usageFrequencies = {
  everyDay: $localize`:@@usageEveryDay:Every day`,
  fewTimesWeek: $localize`:@@usageFewTimesWeek:A few times a week`,
  occasionally: $localize`:@@usageOccasionally:Occasionally, for big documents`,
  rarely: $localize`:@@usageRarely:Rarely, but it has to work well`,
} as const;

export type UsageFrequencyId = keyof typeof usageFrequencies;

export const voicePreferences = {
  friendlyFemale: $localize`:@@voiceFriendlyFemale:Friendly female narrator`,
  calmMale: $localize`:@@voiceCalmMale:Calm male narrator`,
  balancedNeutral: $localize`:@@voiceBalancedNeutral:Balanced, neutral voice`,
  expressiveStoryteller: $localize`:@@voiceExpressive:Expressive storyteller`,
  exploreOptions: $localize`:@@voiceExploreOptions:Let me explore options`,
} as const;

export type VoicePreferenceId = keyof typeof voicePreferences;

export const voicePreferenceOptions = [
  {
    id: 'friendlyFemale',
    title: voicePreferences.friendlyFemale,
    emoji: '🎙️',
  },
  {
    id: 'calmMale',
    title: voicePreferences.calmMale,
    emoji: '📻',
  },
  {
    id: 'balancedNeutral',
    title: voicePreferences.balancedNeutral,
    emoji: '🎧',
  },
  {
    id: 'expressiveStoryteller',
    title: voicePreferences.expressiveStoryteller,
    emoji: '🗣️',
  },
  {
    id: 'exploreOptions',
    title: voicePreferences.exploreOptions,
    emoji: '🔄',
  },
];

export const readerFinishBooksOptions = [
  {
    id: 'rarelyFinish',
    title: $localize`:@@readerFinishRarely:Yes, I rarely finish books`,
  },
  {
    id: 'sometimes',
    title: $localize`:@@readerFinishSometimes:Sometimes, but I try`,
  },
  {
    id: 'usuallyFinish',
    title: $localize`:@@readerFinishUsually:No, I usually finish them`,
  },
];

export const contentFormats = {
  webArticles: $localize`:@@formatWebArticles:🌐 Web Articles & Blogs`,
  ebooks: $localize`:@@formatEbooks:📖 eBooks & Kindle`,
  documentsPdfs: $localize`:@@formatDocsPdfs:📄 Documents & PDFs`,
  emails: $localize`:@@formatEmails:📧 Emails & Newsletters`,
  ownWriting: $localize`:@@formatOwnWriting:✍️ My Own Writing, Notes & Memos`,
  booksPrintedTexts: $localize`:@@formatBooksPrintedTexts:📚 Books & Printed Texts`,
  religiousTexts: $localize`:@@formatReligiousTexts:🙏 Bible & Religious Texts`,
  aiChatsResearch: $localize`:@@formatAiChatsResearch:🤖 AI Chats & Research`,
  youtubeVideos: $localize`:@@formatYoutubeVideos:▶️ YouTube Videos`,
  other: $localize`:@@formatOther:🤔 Other`,
} as const;

export type ContentFormatId = keyof typeof contentFormats;

export const nonFictionInterests = {
  selfImprovement: $localize`:@@nonFictionSelfImprovement:Self-improvement`,
  productivity: $localize`:@@nonFictionProductivity:Productivity`,
  philosophySpirituality: $localize`:@@nonFictionSpiritualityPhilosophy:Spirituality & Philosophy`,
  history: $localize`:@@nonFictionHistory:History`,
  businessCareers: $localize`:@@nonFictionBusinessCareers:Business and careers`,
  relationshipsHealth: $localize`:@@nonFictionRelationshipsHealth:Relationships and health`,
  biographies: $localize`:@@nonFictionBiographies:Biographies`,
  scienceTechnology: $localize`:@@nonFictionScienceTechnology:Science and technology`,
  other: $localize`:@@otherOrNoneOfAbove:Other / none of the above`,
} as const;

export type NonFictionInterestId = keyof typeof nonFictionInterests;

export const fictionInterests = {
  contemporaryRomance: $localize`:@@fictionContemporaryRomance:Contemporary romance`,
  mysteryThriller: $localize`:@@fictionMysteryThriller:Mystery and thriller`,
  historicalFiction: $localize`:@@fictionHistoricalFiction:Historical fiction`,
  romantasy: $localize`:@@fictionRomantasy:Romantasy`,
  fantasy: $localize`:@@fictionFantasy:Fantasy`,
  scienceFiction: $localize`:@@fictionScienceFiction:Science fiction`,
  bookClubPicks: $localize`:@@fictionBookClubPicks:Book club picks`,
  horror: $localize`:@@fictionHorror:Horror`,
  other: $localize`:@@otherOrNoneOfAbove:Other / none of the above`,
} as const;

export type FictionInterestId = keyof typeof fictionInterests;

export const consumptionTimes = {
  inMorning: $localize`:@@timeInMorning:In the morning`,
  workingOut: $localize`:@@timeWorkingOut:While working out`,
  onCommute: $localize`:@@timeCommute:On my commute`,
  takingBreak: $localize`:@@timeTakingBreak:While taking a break`,
} as const;

export type ConsumptionTimeId = keyof typeof consumptionTimes;

export const frustrations = {
  roboticVoices: $localize`:@@frustRoboticVoices:🤖 Voices sound robotic or unnatural`,
  pdfsParsedPoorly: $localize`:@@frustPdfsParsed:❌ PDFs / documents are parsed poorly`,
  formattingMessed: $localize`:@@frustFormatting:🧩 Formatting or structure is messed up`,
  copyPasteWorkflow: $localize`:@@frustCopyPaste:🔄 Annoying copy–paste workflow`,
  ocrUnreliable: $localize`:@@frustOcr:⚠️ OCR for screenshots is unreliable`,
  tooExpensive: $localize`:@@frustExpensive:💰 Too expensive`,
  notEnoughVoices: $localize`:@@frustVoiceOptions:🎧 Not enough voice options / accents`,
  textLimits: $localize`:@@frustTextLimits:📏 Tight limits on amount of text`,
  noneOfThese: $localize`:@@frustNone:💡 None of these`,
} as const;

export type FrustrationId = keyof typeof frustrations;

// Default motivations to use when no goal is selected (IDs only)
export const defaultMotivationIds: MotivationId[] = [
  'personalGrowth',
  'advanceCareer',
  'supportMentalHealth',
  'enjoyEntertainment',
];

// Maps each goal ID to the grammatically correct preposition or phrase
export const goalTitlePhrasing: Record<GoalId, string> = {
  beMoreProductive: $localize`:@@beingMoreProductive:being more productive`,
  learnSomethingNew: $localize`:@@learningSomethingNew:learning something new`,
  listenOnTheGo: $localize`:@@listeningOnTheGo:listening on the go`,
  makeReadingEasier: $localize`:@@makingReadingEasier:making reading easier`,
  everythingIsPerfect: $localize`:@@turningReadingAudio:turning your reading into audio`,
  adhdEyeStrainDyslexia: $localize`:@@overcomingHealthIssues:overcoming health issues`,
};

// Maps each goal ID to relevant motivation IDs
export const goalMotivationsMap: Record<GoalId, MotivationId[]> = {
  beMoreProductive: [
    'supportMentalHealth',
    'saveTime',
    'advanceCareer',
    'improveFocus',
    'personalGrowth',
  ],
  learnSomethingNew: [
    'advanceCareer',
    'excelEducation',
    'personalGrowth',
    'saveTime',
    'supportMentalHealth',
  ],
  listenOnTheGo: [
    'saveTime',
    'enjoyEntertainment',
    'advanceCareer',
    'excelEducation',
    'personalGrowth',
  ],
  makeReadingEasier: [
    'advanceCareer',
    'excelEducation',
    'overcomeDyslexia',
    'reduceEyeStrain',
    'supportMentalHealth',
  ],
  everythingIsPerfect: [
    'advanceCareer',
    'excelEducation',
    'improveFocus',
    'supportMentalHealth',
    'personalGrowth',
  ],
  adhdEyeStrainDyslexia: [
    'advanceCareer',
    'reduceEyeStrain',
    'overcomeDyslexia',
    'manageAdhd',
    'excelEducation',
  ],
};

export interface Celebrity {
  name: string;
  imageFilePath: string;
}

// Celebrity voice roster, actors only: 90-day vote data (Jul 2026) showed
// musicians lose the "do you like this voice" vote badly (Beyonce 38%,
// Billie Eilish 42%, Eminem 31% yes) while actors' famous speaking voices
// win (Morgan Freeman 90% with men, Robin Williams 74%, Natalie Portman
// 71% with women), so the singers were dropped. Lineups per audience live
// in FunnelFlowService.getCelebLineup and its funnel overrides.
export const celebrities = {
  morganFreeman: {
    name: 'Morgan Freeman',
    imageFilePath: 'assets/celebs/morgan_freeman.png',
  },
  robinWilliams: {
    name: 'Robin Williams',
    imageFilePath: 'assets/celebs/robin_williams.png',
  },
  nataliePortman: {
    name: 'Natalie Portman',
    imageFilePath: 'assets/celebs/natalie_portman.png',
  },
  oprahWinfrey: {
    name: 'Oprah Winfrey',
    imageFilePath: 'assets/celebs/oprah_winfrey.png',
  },
  // "Passionate masculine" voices for female audiences - perennial
  // favorites in most-loved-voice polls and rom-com icons for the 45+
  // women segment that drives paid traffic. First tested in the
  // romance funnel lineup; vote results arrive per-name via the
  // asset property on web_quiz_step_finished.
  idrisElba: {
    name: 'Idris Elba',
    imageFilePath: 'assets/celebs/idris_elba.png',
  },
  matthewMcconaughey: {
    name: 'Matthew McConaughey',
    imageFilePath: 'assets/celebs/matthew_mcconaughey.png',
  },
  georgeClooney: {
    name: 'George Clooney',
    imageFilePath: 'assets/celebs/george_clooney.png',
  },
  // The two voices the app actually ships - promoted into the main
  // lineups Aug 2026 when 90-day votes sank Oprah (48% yes with women)
  // and left Robin Williams weakest among the men (75%).
  bradPitt: {
    name: 'Brad Pitt',
    imageFilePath: 'assets/celebs/brad_pitt.png',
  },
  zendaya: {
    name: 'Zendaya',
    imageFilePath: 'assets/celebs/zendaya.png',
  },
};

/*
    rive: {
      fileName: 'people_group_one',
      animations: [],
      ratio: 1024 / 675,
    },
*/

//not used rives: confident, rocket
export const quizSteps = {
  goals: {
    title:
      $localize`:@@quizGoalsTitlePrefix:What do you want to ` +
      highlightText($localize`:@@getBetterAt:get better at`) +
      $localize`:@@quizGoalsTitleSuffix: with us?`,
    subtitle: $localize`:@@quizGoalsSubtitle:Select one area to focus on`,
    options: [
      {
        id: 'beMoreProductive',
        title: goals.beMoreProductive,
      },
      {
        id: 'learnSomethingNew',
        title: goals.learnSomethingNew,
      },
      {
        id: 'listenOnTheGo',
        title: goals.listenOnTheGo,
      },
      {
        id: 'makeReadingEasier',
        title: goals.makeReadingEasier,
      },
      {
        id: 'adhdEyeStrainDyslexia',
        title: goals.adhdEyeStrainDyslexia,
      },
      {
        id: 'everythingIsPerfect',
        title: goals.everythingIsPerfect,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  goalInfo: {
    title: $localize`:@@clearGoalLikelyNail:Now that you have a clear goal, you are 33% more likely* to nail it`,
    subtitle: $localize`:@@peechHelped3Million:Peech has helped over 3 million people continue learning and growing every day. Let us do the same for you.`,
    rive: {
      fileName: 'peech_graph_1_white',
      animations: [],
      ratio: 1024 / 833,
      marginTop: '-10%',
    },
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  gender: {
    title:
      $localize`:@@quizGenderTitlePrefix:Select your ` + highlightText($localize`:@@gender:gender`),
    subtitle: $localize`:@@quizGenderSubtitle:Your sex and hormones affect how you hear speech and voices`,
    options: [
      {
        id: 'male',
        title: genders.male,
        rive: {
          fileName: 'male',
          flip: true,
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
      },
      {
        id: 'female',
        title: genders.female,
        rive: {
          fileName: 'female',
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
      },
      {
        id: 'other',
        title: genders.other,
      },
    ],
    isTilesDisplay: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  age: {
    title: $localize`:@@quizAgeTitle:How old are you?`,
    subtitle: $localize`:@@quizAgeSubtitle:Select your age to start`,
    options: [
      {
        id: 'under21',
        title: ageRanges.under21,
        rive: {
          fileName: 'age_01',
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
        isNextIconShown: true,
      },
      {
        id: 'age2140',
        title: ageRanges.age2140,
        rive: {
          fileName: 'age_02',
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
        isNextIconShown: true,
      },
      {
        id: 'age4154',
        title: ageRanges.age4154,
        rive: {
          fileName: 'age_03',
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
        isNextIconShown: true,
      },
      {
        id: 'age55plus',
        title: ageRanges.age55plus,
        rive: {
          fileName: 'age_04',
          animations: ['Timeline 1'],
          ratio: 1.5 / 1,
        },
        isNextIconShown: true,
      },
      {
        id: 'preferNotToTell',
        title: ageRanges.preferNotToTell,
        isNextIconShown: true,
      },
    ] as QuizOption[],
    isTilesDisplay: true,
    isQuizBadgeShown: false,
    isMultiple: false,
    isRequired: true,
  },

  challenges: {
    title:
      $localize`:@@quizChallengesTitlePrefix:What's ` +
      highlightText($localize`:@@holdingBack:holding back`) +
      $localize`:@@quizChallengesTitleSuffix: your growth?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: Object.entries(challenges).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  amountInfo: {
    title: $localize`:@@joinMillionProfessionals:Join Over 1 Million Professionals Your Age Worldwide`,
    mediaCite: $localize`:@@mediaCitePeech:Peech is a text-to-speech app that actually sounds good - an essential tool for successful individuals.`,
    rive: {
      fileName: 'map',
      animations: ['Both'],
      ratio: 1.4 / 1,
    } as RiveAnimationConfig,
    isRequired: false,
    isMultiple: false,
  },

  professionalStatus: {
    title: $localize`:@@quizProfessionalTitle:What best describes your current professional status?`,
    subtitle: $localize`:@@tailorExperience:It helps us tailor your experience`,
    options: Object.entries(professionalStatuses).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  distraction: {
    title:
      $localize`:@@quizDistractionTitlePrefix:How easily do you ` +
      highlightText($localize`:@@getDistracted:get distracted`) +
      $localize`:@@quizDistractionTitleSuffix: when trying to read or study?`,
    options: Object.entries(distractionLevels).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  anxiety: {
    title:
      $localize`:@@quizAnxietyTitlePrefix:Do you ` +
      highlightText($localize`:@@feelStressedAnxious:feel stressed or anxious`) +
      $localize`:@@quizAnxietyTitleSuffix: about handling responsibilities and personal goals?`,
    options: Object.entries(anxietyLevels).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  procrastination: {
    title: $localize`:@@quizProcrastinationTitle:Do you procrastinate on learning new things?`,
    options: Object.entries(procrastinationFrequencies).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  //   rive: {
  //     fileName: 'impressed',
  //     animations: ['char', 'skirt', 'birds', 'wings'],
  //     ratio: 1.75 / 1,
  //   },

  // interestsInfo: {
  //   title: `You're in Good Company! ${highlightText('Over 1.5 million individuals')} using Peech share similar interests`,
  //   subtitle:
  //     'Stay tuned! Soon, you'll discover how Peech helps you stay connected with what matters to you, both in and outside of work.',
  //   topIcon: '🤝',
  //   userReview: {
  //     message:
  //       'The app is impressive. easy to listening to, no robotic voice, easy to use. productive use of my driving time. would recommend.',
  //     name: 'Jerin Barnes',
  //     avatarUrl: 'assets/img/avatars/m1.jpg',
  //   },
  //   isMultiple: false,
  //   isRequired: false,
  // },

  technology: {
    title:
      $localize`:@@quizTechnologyTitlePrefix:How do you feel about using technology to support ` +
      highlightText($localize`:@@yourGrowth:your growth?`),
    options: Object.entries(technologyReadiness).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  technologyInfo: {
    title: highlightText($localize`:@@stayingUpToDate:Staying Up to Date`),
    subtitle: $localize`:@@transformInfoConsumption:Transform how you consume information with AI! Peech instantly turns reading into audio, fueling your self-growth and ambitious goals.`,
    rive: {
      fileName: 'boy_with_book',
      animations: [],
      ratio: 1024 / 882,
      marginTop: '-20%',
    },
    isMultiple: false,
    isRequired: false,
  },

  audioExperience: {
    title:
      $localize`:@@quizAudioExpTitlePrefix:Have you tried using ` +
      highlightText($localize`:@@audiobooksPodcasts:audiobooks or podcasts?`),
    options: Object.entries(audioExperiences).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  kindleAudioExperience: {
    title:
      $localize`:@@kindleAudioExperienceTitlePrefix:How do you usually get audio for ` +
      highlightText($localize`:@@kindleBooksYouWantToFinish:books you want to finish`) +
      $localize`:@@kindleAudioExperienceTitleSuffix:?`,
    subtitle: $localize`:@@kindleAudioExperienceSubtitle:Choose the closest fit:`,
    options: [
      {
        id: 'yesRegularly',
        title: $localize`:@@kindleAudioExpBuyRegularly:🎧 I buy audiobook versions regularly`,
      },
      {
        id: 'yesOccasionally',
        title: $localize`:@@kindleAudioExpBuySometimes:🎙️ Only sometimes - buying twice adds up`,
      },
      {
        id: 'noButInterested',
        title: $localize`:@@kindleAudioExpWishKindle:👂 I wish my Kindle books were listenable`,
      },
      {
        id: 'noNotReally',
        title: $localize`:@@kindleAudioExpMostlyRead:📖 I mostly read, not listen`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  },

  audioExperienceInfo: {
    title:
      $localize`:@@youLovePeechPrefix:You are going to love ` +
      highlightText($localize`:@@peech:Peech!`),
    subtitle: $localize`:@@peechWorld1App:Peech is the World #1 audio productivity app.\n\nOur powerful AI turns anything you read into audiobook - with human-sounding voices adapted based on your profile.`,
    rive: {
      fileName: 'calm_girl',
      animations: ['Timeline 1'],
      ratio: 1.6 / 1,
    },
    isMultiple: false,
    isRequired: false,
  },

  audioCustomizationInfo: {
    title: highlightText($localize`:@@almostDone:We are almost done!`),
    subtitle: $localize`:@@craftTailoredExperience:We already know a bit about you - now let's use your answers to craft a truly tailored experience!`,
    rive: {
      fileName: 'happy_girl',
      animations: ['Timeline 1'],
      ratio: 1.3 / 1,
    },
    isMultiple: false,
    isRequired: false,
  },

  time: {
    title: $localize`:@@quizTimeTitle:What's your favorite time to read or listen to content?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: [
      {
        id: 'inMorning',
        title: consumptionTimes.inMorning,
        emoji: '☕️',
      },
      {
        id: 'workingOut',
        title: consumptionTimes.workingOut,
        emoji: '🏃‍♂️',
      },
      {
        id: 'onCommute',
        title: consumptionTimes.onCommute,
        emoji: '🚗',
      },
      {
        id: 'takingBreak',
        title: consumptionTimes.takingBreak,
        emoji: '🛋',
      },
      {
        id: 'chores',
        title: $localize`:@@readerTimeChores:While doing chores`,
        emoji: '🧹',
      },
      {
        id: 'workingOrStudying',
        title: $localize`:@@readerTimeWorkStudy:While working or studying`,
        emoji: '💻',
      },
    ],
    isMultiple: true,
    isTilesDisplay: true,
    isRequired: false,
  } as Quiz,

  honest: {
    title: $localize`:@@thanksBeingHonest:Thanks for being honest!`,
    subtitle: $localize`:@@doingGreatNextSteps:You are doing great!\nWe'll consider your answers and continue with the next steps`,
    rive: {
      fileName: 'loved_girl',
      animations: ['Timeline 1'],
      ratio: 1.3 / 1,
    },
    isMultiple: false,
    isRequired: false,
  },

  result: {
    title: $localize`:@@quizDoneTitle:The quiz is done.\nLet's see what we've got! 👍`,
    messages: [
      {
        text: $localize`:@@resultMsg1:Focusing on personal growth is a meaningful step forward. However, balancing work and personal priorities is a common challenge. Like many people, you sometimes experience stress and struggle with procrastination — and that's completely normal.`,
      },
      {
        text: $localize`:@@resultMsg2:🎧 Using cutting-edge tools and audio for learning is essential nowadays and can significantly enhance your growth.`,
      },
      {
        text: $localize`:@@resultMsg3:📈 Your Personal Growth Index (PGI) is currently estimated at 57%. Dedicating just 10–15 minutes a day can boost your PGI from 57% to 93% in three months.`,
      },
      {
        text: $localize`:@@resultMsg4:💪 With Peech, you can turn a few minutes a day into real results—and we've got a special offer to help you get there faster.`,
      },
    ],
    isRequired: true,
    isMultiple: false,
  } as Quiz,

  book1: {
    title: $localize`:@@bookListenTitle:Would you like to listen to this book?`,
    subtitle:
      $localize`:@@hitPlayBelowPrefix:Hit ` +
      '<span class="ri-play-fill main-color"></span>' +
      $localize`:@@hitPlayBelowSuffix: below 👇`,
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  book2: {
    title: $localize`:@@bookListenTitle:Would you like to listen to this book?`,
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  book3: {
    title: $localize`:@@bookListenTitle:Would you like to listen to this book?`,
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  yourBook: {
    title: $localize`:@@bookListenTitle:Would you like to listen to this book?`,
    imageConfig: {
      path: 'assets/en/books/your_next_reading.jpg',
    },
    audioTracks: [
      {
        title: $localize`:@@anythingYouWant:Anything you want`,
        audioPath: 'assets/en/sounds/your_next_reading.mp3',
        imagePath: 'assets/img/avatars/6.png',
      },
    ],
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  visionCare: {
    title: $localize`:@@quizVisionTitle:Do you ever worry your eyesight is getting worse?`,
    subtitle: $localize`:@@beHonest:Be honest:`,
    options: Object.entries(visionCareOptions).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  visionCareInfo: {
    title: $localize`:@@eyesDoEnough:Your eyes do enough already`,
    subtitle: $localize`:@@listeningEyesBreak:Listening gives your eyes a break, without stopping you.`,
    rive: {
      fileName: 'boy_headphones',
      animations: ['Timeline 1'],
      ratio: 1.3 / 1,
    },
  } as Quiz,

  timeForReading: {
    title:
      $localize`:@@quizTimeForReadingTitlePrefix:Do you ever say ` +
      highlightText($localize`:@@noTimeToRead:"I don't have time to read"`) +
      $localize`:@@quizTimeForReadingTitleSuffix:?`,
    options: Object.entries(timeForReadingOptions).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  kindleFriction: {
    title:
      $localize`:@@kindleFrictionTitlePrefix:What keeps your ` +
      highlightText($localize`:@@kindleBooksUnfinished:Kindle books unfinished`) +
      $localize`:@@kindleFrictionTitleSuffix:?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: Object.entries(kindleFrictionOptions).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  timeForReadingInfo: {
    title: $localize`:@@quiteCommon:It is quite common 🤓`,
    subtitle: (() => {
      const books = $localize`:@@booksCompleted50k:50,000+ books`;
      const boldBooks = `<strong>${books}</strong>`;
      return (
        $localize`:@@lackTimeReadReasonLine1:Lack of time to read is one of the main reasons for the rapid growth of audio formats.` +
        '\n' +
        $localize`:@@lackTimeReadReasonLine2Prefix:Peech users who struggle to find time for reading completed ` +
        boldBooks +
        $localize`:@@lackTimeReadReasonLine2Suffix: last month.`
      );
    })(),
    rive: {
      fileName: 'map',
      animations: ['Both'],
      ratio: 1.4 / 1,
    },
  } as Quiz,

  retention: {
    title:
      $localize`:@@quizRetentionTitlePrefix:Do you ever feel like you spend time reading but ` +
      highlightText($localize`:@@dontRemember:don't remember`) +
      $localize`:@@quizRetentionTitleSuffix: much?`,
    options: Object.entries(retentionOptions).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  retentionInfo: {
    title: highlightText($localize`:@@didYouKnow:Did You Know?`),
    subtitle: $localize`:@@combiningReadingBoosts:Combining reading and listening boosts retention by up to 70%. That's why so many people love Peech—it turns reading into engaging audio for better results.`,
    rive: {
      fileName: 'boy_with_book_outside',
      animations: ['Timeline 1'],
      ratio: 1.1 / 1,
      marginTop: '-10%',
    },
    isMultiple: false,
    isRequired: false,
  },

  investmentTime: {
    title:
      highlightText($localize`:@@amazingChoice:Amazing choice`) +
      $localize`:@@seriousPersonSuffix:, you seem like a serious person!`,
    subtitle: $localize`:@@howMuchTimeInvest:How much time can you invest in becoming the better version of yourself?`,
    options: Object.entries(investmentTimes).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  listeningTime: {
    title:
      highlightText($localize`:@@amazingChoice:Amazing choice`) +
      $localize`:@@listeningGoalSuffix:, let's set your listening goal!`,
    subtitle: $localize`:@@howMuchTimeListening:Pick the amount of time you'd like to spend listening daily`,
    options: Object.entries(listeningTimes).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  },

  prediction: {
    title: $localize`:@@wePredictYoull:We predict you'll`,
    subtitle: $localize`:@@consumeRetainMore:be able to consume more content and retain more information in the next 3 months`,
    rive: {
      fileName: 'peech_graph_2_white',
      animations: [],
      ratio: 1.4 / 1,
    },
    isMultiple: false,
    isRequired: false,
  },

  // Static defaults only - FunnelFlowService.getCelebLineup (and funnel
  // overrides) always replace subtitle/imageConfig per audience.
  celeb1: {
    title: $localize`:@@likeVoicePerson:Do you like the voice of this person?`,
    subtitle: celebrities.morganFreeman.name,
    imageConfig: {
      path: celebrities.morganFreeman.imageFilePath,
    },
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  celeb2: {
    title: $localize`:@@likeVoicePerson:Do you like the voice of this person?`,
    subtitle: celebrities.nataliePortman.name,
    imageConfig: {
      path: celebrities.nataliePortman.imageFilePath,
    },
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  celeb3: {
    title: $localize`:@@likeVoicePerson:Do you like the voice of this person?`,
    subtitle: celebrities.bradPitt.name,
    imageConfig: {
      path: celebrities.bradPitt.imageFilePath,
    },
    isVoting: true,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  // adhdSymptoms: {
  //   title: `Which ${highlightText('ADHD symptoms')} do you think you may have?`,
  //   subtitle: 'Select all that apply:',
  //   options: [
  //     `Distractibility`,
  //     `Disorganization`,
  //     `Inattention`,
  //     `Impulsivity`,
  //     `Executive Dysfunction`,
  //     `Other`,
  //   ].map(title => ({ title })),
  //   isMultiple: true,
  //   isRequired: true,
  // },

  // adhdSymptomsImpact: {
  //   title: `What are the main areas you feel ADHD symptoms may have an impact on?`,
  //   subtitle: 'Select all that apply:',
  //   options: [
  //     `Work performance`,
  //     `Emotional regulation`,
  //     `Sleep patterns`,
  //     `Social interactions`,
  //     `Academic performance`,
  //     `Self-esteem`,
  //     `Other`,
  //   ].map(title => ({ title })),
  //   isMultiple: true,
  //   isRequired: true,
  // },

  format: {
    title: $localize`:@@quizFormatTitle:What would you want to turn into audio?`,
    options: Object.entries(contentFormats).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  nonFictionInterests: {
    title: $localize`:@@quizNonFictionInterestsTitle:What types of non-fiction are you interested in?`,
    subtitle: $localize`:@@chooseAsManyAsYouLike:Choose as many as you like`,
    options: Object.entries(nonFictionInterests).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  fictionInterests: {
    title: $localize`:@@quizFictionInterestsTitle:What types of fiction books do you enjoy the most?`,
    subtitle: $localize`:@@chooseAsManyAsYouLike:Choose as many as you like`,
    options: Object.entries(fictionInterests).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  personType: {
    title: $localize`:@@quizPersonTypeTitle:What do you consider yourself?`,
    options: Object.entries(personTypes).map(([id, title]) => ({ id, title })),
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  personTypeInfo: {
    title: $localize`:@@welcomeAmbivertClub:Welcome to the Ambivert Club!`,
    subtitle: $localize`:@@ambivertExplanationVoiceTraining:People who identify as extroverts or introverts based on the situation are termed ambiverts. With this in mind, we will tailor a unique approach to your communication and voice training.`,
    rive: {
      fileName: 'ambivert',
      animations: ['Timeline 1'],
      ratio: 1.75 / 1,
    },
  } as Quiz,

  // resultInfo: {
  //   title: `${highlightText('87% of Peech users')} have made positive changes in their lives`,
  //   subtitle: 'You're already on your way to positive change, too! Let's see what you are interested in exactly.',
  //   topIcon: '🤓',
  //   isRequired: false,
  //   isMultiple: false,
  // },
  // speedInfo: {
  //   title: `How about getting it ${highlightText('a bit faster?')}`,
  //   subtitle: 'Let's try this on x1.5 and x2 speed. Using speed contol is true productivity boost and one of favorite features among our users',
  //   audioTracks: [audioTracks.medicine, audioTracks.physics],
  //   isRequired: false,
  //   isMultiple: false,
  // },
  // speedStatsInfo: {
  //   title: `With Peech you can save ${highlightText('at least 2 hours')} on reading this week!`,
  //   topIcon: '⏱',
  //   userReview: {
  //     message: 'The app is impressive. easy to listening to, no robotic voice, easy to use. productive use of my driving time. would recommend.',
  //     name: 'Jerin Barnes',
  //     avatarUrl: 'assets/img/avatars/5.png',
  //   },
  //   isRequired: false,
  //   isMultiple: false,
  // },

  goalMotivations: {
    title:
      $localize`:@@quizGoalMotivTitlePrefix:What do you hope to ` +
      highlightText($localize`:@@achieve:achieve`) +
      $localize`:@@quizGoalMotivTitleSuffix: with this goal?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: [], // Dynamically populated in service based on selected goal
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  demo1: {
    title: $localize`:@@demo1:Demo 1`,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  demo2: {
    title: $localize`:@@demo2:Demo 2`,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  demo3: {
    title: $localize`:@@demo3:Demo 3`,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  demo4: {
    title: $localize`:@@demo4:Demo 4`,
    isMultiple: false,
    isRequired: false,
  } as Quiz,

  usageFrequency: {
    title:
      $localize`:@@quizUsageFreqTitlePrefix:How often would you use ` +
      highlightText($localize`:@@textToSpeech:text-to-speech`) +
      $localize`:@@quizUsageFreqTitleSuffix:?`,
    subtitle: $localize`:@@tuneForWorkflow:This helps us tune Peech for your workflow`,
    options: [
      {
        id: 'everyDay',
        title: usageFrequencies.everyDay,
        emoji: '🔥',
      },
      {
        id: 'fewTimesWeek',
        title: usageFrequencies.fewTimesWeek,
        emoji: '📅',
      },
      {
        id: 'occasionally',
        title: usageFrequencies.occasionally,
        emoji: '📂',
      },
      {
        id: 'rarely',
        title: usageFrequencies.rarely,
        emoji: '📘',
      },
    ],
    isTilesDisplay: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  frustrations: {
    title:
      $localize`:@@quizFrustrationsTitlePrefix:What usually ` +
      highlightText($localize`:@@worriesYou:worries you`) +
      $localize`:@@quizFrustrationsTitleSuffix: when trying tools like this?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: Object.entries(frustrations).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  voicePreference: {
    title:
      $localize`:@@quizVoicePrefTitlePrefix:Which type of ` +
      highlightText($localize`:@@voice:voice`) +
      $localize`:@@quizVoicePrefTitleSuffix: do you prefer?`,
    subtitle: $localize`:@@changeThisLater:You can always change this later.`,
    options: voicePreferenceOptions,
    isTilesDisplay: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  // Opens the reader and romance funnels. The message id predates the step's rename and is
  // kept so the seven existing translations still apply.
  wishReadAloud: {
    title: $localize`:@@readerAudioExperienceTitle:Wish someone could read for you?`,
    options: [
      {
        id: 'yes',
        title: $localize`:@@yes:Yes`,
        rive: {
          fileName: 'make_reading_easier',
          animations: ['Timeline 1'],
          ratio: 1.3 / 1,
        },
      },
      {
        id: 'no',
        title: $localize`:@@no:No`,
        rive: {
          fileName: 'learn_something_new',
          animations: ['Timeline 1'],
          ratio: 1.3 / 1,
        },
      },
    ],
    isTilesDisplay: true,
    hasSocialProof: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  // Opens the main funnel in the funnel_first_screen_nov_2026 test. The flow service moves this
  // question to the subtitle under the intro title, as for hasKindleBooks.
  readingToAudio: {
    title: $localize`:@@readingToAudioTitle:Would you like to turn what you read into audio?`,
    options: [
      {
        id: 'yes',
        title: $localize`:@@yes:Yes`,
        rive: {
          fileName: 'boy_with_book',
          animations: ['Timeline 1'],
          ratio: 1.4 / 1,
        },
      },
      {
        id: 'no',
        title: $localize`:@@no:No`,
        rive: {
          fileName: 'sad_girl',
          animations: ['Timeline 1'],
          ratio: 1.4 / 1,
          shiftDown: '10%',
        },
      },
    ],
    isTilesDisplay: true,
    isMentionedInShown: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  // Opens the kindle funnel. KindleFunnelFlowService moves this question to the subtitle under
  // the intro title.
  hasKindleBooks: {
    title: $localize`:@@hasKindleBooksTitle:Do you have Kindle books?`,
    options: [
      {
        id: 'yes',
        title: $localize`:@@yes:Yes`,
        rive: {
          fileName: 'boy_with_book',
          animations: ['Timeline 1'],
          ratio: 1.4 / 1,
        },
      },
      {
        id: 'no',
        title: $localize`:@@no:No`,
        rive: {
          fileName: 'sad_girl',
          animations: ['Timeline 1'],
          ratio: 1.4 / 1,
          shiftDown: '10%',
        },
      },
    ],
    isTilesDisplay: true,
    isMentionedInShown: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  readerGoals: {
    title: $localize`:@@readerGoalsTitle:What are your main reasons for listening to books?`,
    subtitle: $localize`:@@selectAllApply:Select all that apply:`,
    options: Object.entries(readerGoals).map(([id, title]) => ({ id, title })),
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  readerBooksRead: {
    title: $localize`:@@readerBooksReadTitle:How many books have you read in the past year?`,
    options: [
      {
        id: 'oneFive',
        title: $localize`:@@readerBooksReadOneFive:1-5`,
      },
      {
        id: 'sixTwelve',
        title: $localize`:@@readerBooksReadSixTwelve:6-12`,
      },
      {
        id: 'moreThanTwelve',
        title: $localize`:@@readerBooksReadMoreTwelve:More than 12`,
      },
      {
        id: 'none',
        title: $localize`:@@readerBooksReadNone:None`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  readerTimeForReading: {
    title:
      $localize`:@@readerTimeForReadingTitlePrefix:Do you often feel ` +
      highlightText(
        $localize`:@@readerTimeForReadingTitleHighlight:distracted or lose focus`,
        true
      ) +
      $localize`:@@readerTimeForReadingTitleSuffix: while reading?`,
    options: [
      {
        id: 'often',
        title: $localize`:@@readerDistractedOften:Yes, often`,
      },
      {
        id: 'sometimes',
        title: $localize`:@@readerDistractedSometimes:Occasionally`,
      },
      {
        id: 'no',
        title: $localize`:@@readerDistractedNo:No, I can stay focused`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  readerFinishBooks: {
    title:
      $localize`:@@readerFinishTitlePrefix:Do you find it ` +
      highlightText($localize`:@@readerFinishTitleHighlight:hard to finish`, true) +
      $localize`:@@readerFinishTitleSuffix: the books you start?`,
    options: readerFinishBooksOptions,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  readerReadingOverwhelm: {
    title:
      $localize`:@@readerOverwhelmingTitlePrefix:Does reading feel ` +
      highlightText($localize`:@@readerOverwhelmingTitleHighlight:tiring or overwhelming`, true) +
      $localize`:@@readerOverwhelmingTitleSuffix: for you?`,
    options: [
      {
        id: 'exhausting',
        title: $localize`:@@readerOverwhelmingYes:Yes, it is exhausting`,
      },
      {
        id: 'longTexts',
        title: $localize`:@@readerOverwhelmingSometimes:Sometimes, with long texts`,
      },
      {
        id: 'enjoyReading',
        title: $localize`:@@readerOverwhelmingNo:No, I enjoy reading`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  // Steps for the romance funnel (romance-funnel, RomanceFunnelFlowService)

  // Reassurance interstitial after the friction question. The subtitle is
  // personalized at runtime for users who picked the fall-asleep
  // friction (see RomanceFunnelFlowService) - falling asleep mid-chapter is
  // the friction this reassurance is built around.
  romanceHonestInfo: {
    title: $localize`:@@thanksBeingHonest:Thanks for being honest!`,
    // Default subtitle for readers who picked a different friction; see
    // RomanceFunnelFlowService.customizeStepData for the personalized copy.
    subtitle: $localize`:@@romanceHonestSubtitleDefault:None of this means you love books any less.\nIt just means your books need to meet you where your day actually is - and that's exactly what we'll set up.`,
    rive: {
      fileName: 'loved_girl',
      animations: ['Timeline 1'],
      ratio: 1.3 / 1,
    },
    isMultiple: false,
    isRequired: false,
  },

  // Voice quality is a top-two friction across the sample this funnel drew
  // its data from (see RomanceFunnelFlowService). Asked right after
  // personTypeInfo (whose copy promises voice matching) and deliberately
  // far from the celeb/book sample screens: the sample narrators are fixed
  // per asset, so a mismatched voice right after this answer would read as
  // ignoring the choice.
  romanceVoicePreference: {
    title:
      $localize`:@@romanceVoicePrefTitlePrefix:Which kind of ` +
      highlightText($localize`:@@romanceVoicePrefTitleHighlight:voice`) +
      $localize`:@@romanceVoicePrefTitleSuffix: should read to you?`,
    subtitle: $localize`:@@romanceVoicePrefSubtitleV2:You can change this any time.`,
    options: voicePreferenceOptions,
    isTilesDisplay: true,
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  romanceSubgenres: {
    title:
      $localize`:@@romanceSubgenresTitlePrefixV2:What's your ` +
      highlightText($localize`:@@romanceSubgenresTitleHighlightV2:kind`) +
      $localize`:@@romanceSubgenresTitleSuffixV2: of love story?`,
    subtitle: $localize`:@@romanceSubgenresSubtitle:Pick as many as you like.`,
    options: [
      {
        id: 'contemporaryRomance',
        title: $localize`:@@romanceSubgenreContemporaryV2:Modern love stories`,
      },
      {
        id: 'romantasy',
        title: $localize`:@@romanceSubgenreRomantasyV2:Fantasy and dragons`,
      },
      {
        id: 'darkRomance',
        title: $localize`:@@romanceSubgenreDarkV2:Dark and dangerous`,
      },
      {
        id: 'historicalRomance',
        title: $localize`:@@romanceSubgenreHistoricalV2:Historical`,
      },
      {
        id: 'smallTownRomance',
        title: $localize`:@@romanceSubgenreSmallTownV2:Cozy small-town`,
      },
      {
        id: 'other',
        title: $localize`:@@romanceSubgenreOtherV2:Surprise me`,
      },
    ],
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  romanceBooksPerMonth: {
    title:
      $localize`:@@romanceBooksPerMonthTitlePrefix:How many books do you ` +
      highlightText($localize`:@@romanceBooksPerMonthTitleHighlight:get through`) +
      $localize`:@@romanceBooksPerMonthTitleSuffix: a month?`,
    subtitle: $localize`:@@romanceBooksPerMonthSubtitleV2:No judgment, we all have a pile waiting.`,
    options: [
      {
        id: 'oneOrFewer',
        title: $localize`:@@romanceBooksPerMonthOneOrFewer:1 or fewer`,
      },
      {
        id: 'twoToThree',
        title: $localize`:@@romanceBooksPerMonthTwoToThree:2 to 3`,
      },
      {
        id: 'fourToSix',
        title: $localize`:@@romanceBooksPerMonthFourToSix:4 to 6`,
      },
      {
        id: 'lostCount',
        title: $localize`:@@romanceBooksPerMonthLostCount:Lost count`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  romanceBookSource: {
    title:
      $localize`:@@romanceBookSourceTitlePrefix:Where do your ` +
      highlightText($localize`:@@romanceBookSourceTitleHighlight:books live`) +
      $localize`:@@romanceBookSourceTitleSuffix:?`,
    options: [
      {
        id: 'kindle',
        title: $localize`:@@romanceBookSourceKindle:Kindle`,
      },
      {
        id: 'ebookFiles',
        title: $localize`:@@romanceBookSourceEbookFiles:EPUB or PDF files`,
      },
      {
        id: 'online',
        title: $localize`:@@romanceBookSourceOnline:Websites and fanfiction`,
      },
      {
        id: 'paperbacks',
        title: $localize`:@@romanceBookSourcePaperbacksV2:Paper books`,
      },
      {
        id: 'mix',
        title: $localize`:@@romanceBookSourceMixV2:A bit of everything`,
      },
    ],
    isMultiple: true,
    isRequired: true,
  } as Quiz,

  romanceSpiceLevel: {
    title:
      $localize`:@@romanceSpiceLevelTitlePrefix:How much ` +
      highlightText($localize`:@@romanceSpiceLevelTitleHighlight:heat`) +
      $localize`:@@romanceSpiceLevelTitleSuffix: do you like in a story?`,
    subtitle: $localize`:@@romanceSpiceLevelSubtitle:No wrong answers, and we won't tell.`,
    options: [
      {
        id: 'sweet',
        title: $localize`:@@romanceSpiceSweetV2:Sweet and closed-door 🍯`,
      },
      {
        id: 'warm',
        title: $localize`:@@romanceSpiceWarmV2:A little steam 🌶️`,
      },
      {
        id: 'steamy',
        title: $localize`:@@romanceSpiceSteamy:Steamy 🌶️🌶️`,
      },
      {
        id: 'noLimits',
        title: $localize`:@@romanceSpiceNoLimitsV2:Bring it on 🌶️🌶️🌶️`,
      },
    ],
    isMultiple: false,
    isRequired: true,
  } as Quiz,

  romanceDiscovery: {
    title:
      $localize`:@@romanceDiscoveryTitlePrefix:How do you find your next ` +
      highlightText($localize`:@@romanceDiscoveryTitleHighlight:book crush`) +
      $localize`:@@romanceDiscoveryTitleSuffix:?`,
    options: [
      {
        id: 'bookTok',
        title: $localize`:@@romanceDiscoveryBookTok:BookTok`,
      },
      {
        id: 'goodreads',
        title: $localize`:@@romanceDiscoveryGoodreads:Goodreads`,
      },
      {
        id: 'friends',
        title: $localize`:@@romanceDiscoveryFriends:Friends`,
      },
      {
        id: 'bookClub',
        title: $localize`:@@romanceDiscoveryBookClub:Book club`,
      },
      {
        id: 'kindleSuggestions',
        title: $localize`:@@romanceDiscoveryKindleSuggestions:Kindle suggestions`,
      },
    ],
    isMultiple: true,
    isRequired: false,
  } as Quiz,
};
